/**
 * LanguageSelect Component
 *
 * Language selector as a code button (e.g., "EN") with dropdown menu
 * Uses translations API data (no hardcoded language lists)
 *
 * Built with shadcn DropdownMenu for consistent styling and accessibility
 */

import React from 'react'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@innolab-trader/ui'
import { useLanguage } from '../LanguageProvider'

export interface LanguageSelectProps {
  className?: string
  /** Show English translation subtitle for non-English languages */
  showEnglishSubtitle?: boolean
}

/**
 * Language metadata with English translations
 * Dynamically derived from availableLanguages
 */
const LANGUAGE_METADATA: Record<string, { native: string; english: string }> = {
  en: { native: 'English', english: 'English' },
  es: { native: 'Español', english: 'Spanish' },
  fr: { native: 'Français', english: 'French' },
  de: { native: 'Deutsch', english: 'German' },
  pt: { native: 'Português', english: 'Portuguese' },
  zh: { native: '中文', english: 'Chinese' },
  ja: { native: '日本語', english: 'Japanese' },
  ru: { native: 'Русский', english: 'Russian' },
}

/**
 * LanguageSelect Component
 *
 * Automatically uses availableLanguages from LanguageProvider context
 * No props needed for basic usage - fully self-contained
 */
export const LanguageSelect: React.FC<LanguageSelectProps> = ({
  className = '',
  showEnglishSubtitle = true,
}) => {
  const { language, setLanguage, availableLanguages } = useLanguage()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`px-2 py-1.5 text-xs font-semibold ${className}`}
          aria-label="Select language"
        >
          {language?.toUpperCase?.() || 'EN'}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuRadioGroup
          value={language}
          onValueChange={(value) => setLanguage(value as any)}
        >
          {availableLanguages.map((option) => {
            const metadata = LANGUAGE_METADATA[option.code]
            const showSubtitle = showEnglishSubtitle && option.code !== 'en' && metadata

            return (
              <DropdownMenuRadioItem
                key={option.code}
                value={option.code}
                className="flex items-start gap-2 py-2"
              >
                {/* Language Code */}
                <span className="mt-0.5 inline-block w-8 text-xs font-semibold text-muted-foreground">
                  {option.code.toUpperCase()}
                </span>

                {/* Language Label + Subtitle */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">
                    {option.label}
                  </div>
                  {showSubtitle && metadata && (
                    <div className="text-xs text-muted-foreground truncate">
                      {metadata.english}
                    </div>
                  )}
                </div>
              </DropdownMenuRadioItem>
            )
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
